import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../../config';
import empresasApi from '../../../api/empresasApi';
import usuariosApi from '../../../api/usuariosApi';

function FormularioUsuario() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [usuario, setUsuario] = useState({
    username: '',
    password_hash: '',
    id_empresa: '',
    id_pais: '',
    id_tipo_autenticacion: 1,
    id_nivel_usuario: ''
  });

  const [empresas, setEmpresas] = useState([]);
  const [paises, setPaises] = useState([]);
  const [tiposAutenticacion, setTiposAutenticacion] = useState([]);
  const [nivelesUsuario, setNivelesUsuario] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      setLoadingData(true);

      // Cargar datos en paralelo
      await Promise.all([
        cargarEmpresas(),
        cargarPaises(),
        cargarTiposAutenticacion(),
        cargarNivelesUsuario()
      ]);

      // Si es edición, cargar datos del usuario
      if (id) {
        await cargarUsuario(id);
      }

      setLoadingData(false);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos del formulario');
      setLoadingData(false);
    }
  };

  const cargarEmpresas = async () => {
    try {
      const response = await empresasApi.listar();
      console.log('Empresas cargadas:', response);
      setEmpresas(response.data || []);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      setEmpresas([]);
    }
  };

  const cargarPaises = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/paises`);
      setPaises(response.data || []);
    } catch (error) {
      console.error('Error al cargar países:', error);
      setPaises([]);
    }
  };

  const cargarTiposAutenticacion = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/general/tipoautenticacion`);
      setTiposAutenticacion(response.data || []);
    } catch (error) {
      console.error('Error al cargar tipos de autenticación:', error);
      setTiposAutenticacion([]);
    }
  };

  const cargarNivelesUsuario = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/general/nivelusuario`);
      setNivelesUsuario(response.data || []);
    } catch (error) {
      console.error('Error al cargar niveles de usuario:', error);
      setNivelesUsuario([]);
    }
  };

  const cargarUsuario = async (id) => {
    try {
      const usuario = await usuariosApi.obtener(id);
      setUsuario(usuario);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
      setError('Error al cargar los datos del usuario');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUsuario(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validarFormulario = () => {
    if (!usuario.username || usuario.username.trim() === '') {
      setError('El nombre de usuario es requerido');
      return false;
    }

    if (!id && (!usuario.password_hash || usuario.password_hash.trim() === '')) {
      setError('La contraseña es requerida');
      return false;
    }

    if (!id && usuario.password_hash && usuario.password_hash.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (!usuario.id_empresa) {
      setError('Debe seleccionar una empresa');
      return false;
    }

    if (!usuario.id_pais) {
      setError('Debe seleccionar un país');
      return false;
    }

    if (!usuario.id_nivel_usuario) {
      setError('Debe seleccionar un nivel de usuario');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (id) {
        await usuariosApi.actualizar(id, usuario);
        setSuccessMessage('Usuario actualizado correctamente');
      } else {
        await usuariosApi.crear(usuario);
        setSuccessMessage('Usuario creado correctamente');
      }

      setTimeout(() => {
        navigate('/admin/usuarios');
      }, 1500);
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      setError(error.response?.data?.error || 'Error al guardar el usuario');
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  if (loadingData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/admin/usuarios')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {id ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Username */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de Usuario *"
                name="username"
                value={usuario.username}
                onChange={handleChange}
                disabled={loading}
                required
                helperText="Nombre de usuario para iniciar sesión"
              />
            </Grid>

            {/* Password - solo en crear */}
            {!id && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contraseña *"
                  name="password_hash"
                  type={showPassword ? 'text' : 'password'}
                  value={usuario.password_hash}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  helperText="Mínimo 6 caracteres"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowPassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            )}

            {/* Empresa */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Empresa *</InputLabel>
                <Select
                  name="id_empresa"
                  value={usuario.id_empresa}
                  onChange={handleChange}
                  disabled={loading}
                  label="Empresa *"
                >
                  <MenuItem value="">
                    <em>Seleccione una empresa</em>
                  </MenuItem>
                  {empresas.map((empresa) => (
                    <MenuItem key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Empresa a la que pertenece el usuario
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* País */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>País *</InputLabel>
                <Select
                  name="id_pais"
                  value={usuario.id_pais}
                  onChange={handleChange}
                  disabled={loading}
                  label="País *"
                >
                  <MenuItem value="">
                    <em>Seleccione un país</em>
                  </MenuItem>
                  {paises.map((pais) => (
                    <MenuItem key={pais.id_pais} value={pais.id_pais}>
                      {pais.det_pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Nivel de Usuario */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Nivel de Usuario *</InputLabel>
                <Select
                  name="id_nivel_usuario"
                  value={usuario.id_nivel_usuario}
                  onChange={handleChange}
                  disabled={loading}
                  label="Nivel de Usuario *"
                >
                  <MenuItem value="">
                    <em>Seleccione un nivel</em>
                  </MenuItem>
                  {nivelesUsuario.map((nivel) => (
                    <MenuItem key={nivel.id_nivel_usuario} value={nivel.id_nivel_usuario}>
                      {nivel.det_nivel_usuario}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Define los permisos del usuario
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Tipo de Autenticación */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Autenticación</InputLabel>
                <Select
                  name="id_tipo_autenticacion"
                  value={usuario.id_tipo_autenticacion}
                  onChange={handleChange}
                  disabled={loading}
                  label="Tipo de Autenticación"
                >
                  {tiposAutenticacion.map((tipo) => (
                    <MenuItem key={tipo.id_tipo_autenticacion} value={tipo.id_tipo_autenticacion}>
                      {tipo.det_tipo_autenticacion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Botones */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/usuarios')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
                    fontWeight: 'bold',
                    px: 4
                  }}
                >
                  {loading ? 'Guardando...' : id ? 'Actualizar Usuario' : 'Crear Usuario'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}

export default FormularioUsuario;
